/* Learning panel — candidate lessons harvested from agent runs, awaiting admin review */

import { esc, escAttr, setPanelLoading } from "./state.js";
import { api } from "./api.js";
import { confirmTyped } from "./dialogs.js";
import { notify } from "./toast.js";
import { buildApproveBody, isScoped, scopeLabel } from "./learning-scope.js";

// ---------------------------------------------------------------------------
// Container helper
// ---------------------------------------------------------------------------

function _container() {
  return document.getElementById("panel-learning");
}

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let _tab = "candidate";   // "candidate" | "promoted" — active list tab
let _detailReq = 0;       // monotonic stale-fetch guard, shared by list refresh + detail opens
let _lastFact = null;     // the fact behind the currently-open card (edit prefill, approve gate)

// ---------------------------------------------------------------------------
// List rendering
// ---------------------------------------------------------------------------

function _truncate(str, maxLen) {
  if (!str) return "";
  return str.length > maxLen ? str.slice(0, maxLen) + "…" : str;
}

function _tabsHtml() {
  const tabBtn = (id, label) =>
    `<button type="button" class="lrn-tab${id === _tab ? " active" : ""}" data-tab="${id}">${label}</button>`;
  return `<div class="lrn-tabs">${tabBtn("candidate", "Candidates")}${tabBtn("promoted", "Promoted")}</div>`;
}

function _renderList(facts, tab) {
  if (!facts.length) {
    const msg = tab === "promoted" ? "No promoted lessons yet." : "No candidate lessons yet.";
    return `<div class="learning-empty">${msg}</div>`;
  }

  return facts.map(fact => {
    const gotchaFull = fact.gotcha || "";
    const gotcha = _truncate(gotchaFull, 140);
    const status = String(fact.status || "candidate");
    const rediscoveries = Number(fact.rediscovery_count || 1);
    const safetyBadge = fact.safety_flagged
      ? `<span class="lrn-safety-badge" title="Flagged for safety review">Safety</span>`
      : "";
    const rediscoveryChip = rediscoveries > 1
      ? `<span class="lrn-count-badge">${esc(String(rediscoveries))}×</span>`
      : "";

    return `<div class="lrn-row"
      data-learning-id="${escAttr(fact.id || "")}">
      <span class="lrn-status-badge lrn-status-${escAttr(status)}">${esc(status)}</span>
      ${safetyBadge}
      <span class="lrn-scope-badge lrn-scope-${escAttr(scopeLabel(fact))}"
        title="${escAttr(scopeLabel(fact) === "connection"
          ? "Private to one connection" : "Global platform knowledge")}">${esc(scopeLabel(fact))}</span>
      ${rediscoveryChip}
      <span class="lrn-gotcha" title="${escAttr(gotchaFull)}">${esc(gotcha)}</span>
    </div>`;
  }).join("");
}

// ---------------------------------------------------------------------------
// Card (drill-in) rendering
// ---------------------------------------------------------------------------

function _fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function _renderHeader(fact) {
  const status = String(fact.status || "candidate");
  const safetyBadge = fact.safety_flagged
    ? `<span class="lrn-safety-badge" title="Flagged for safety review">SAFETY</span>`
    : "";
  const created = fact.created_at ? `Created ${_fmtDate(fact.created_at)}` : "";
  const updated = (fact.updated_at && fact.updated_at !== fact.created_at)
    ? `Updated ${_fmtDate(fact.updated_at)}` : "";
  const stamp = [created, updated].filter(Boolean).join(" · ");

  return `
    <div class="lrn-card-header">
      <div class="lrn-card-title">${esc(fact.gotcha || "")}</div>
      <div class="lrn-card-badges">
        <span class="lrn-status-badge lrn-status-${escAttr(status)}">${esc(status)}</span>
        ${safetyBadge}
        <span class="lrn-scope-badge lrn-scope-${escAttr(scopeLabel(fact))}">${esc(scopeLabel(fact))}</span>
      </div>
      ${stamp ? `<div class="lrn-card-dates">${esc(stamp)}</div>` : ""}
    </div>`;
}

function _field(label, valueHtml) {
  return `<div class="lrn-field"><div class="lrn-field-label">${esc(label)}</div><div class="lrn-field-value">${valueHtml}</div></div>`;
}

function _renderLesson(fact) {
  const parts = [];
  parts.push(_field("Gotcha", esc(fact.gotcha || "")));
  parts.push(_field("Workaround", esc(fact.workaround || "")));
  if (fact.api_shape) parts.push(_field("API shape", `<span class="lrn-mono">${esc(fact.api_shape)}</span>`));
  if (fact.tool) parts.push(_field("Tool", `<span class="lrn-mono">${esc(fact.tool)}</span>`));
  if (isScoped(fact)) {
    parts.push(_field("Scope", `<span class="lrn-mono">${esc(fact.connection_id || "")}</span>`
      + ` <span class="lrn-muted">private to this connection</span>`));
  }
  const triggers = Array.isArray(fact.trigger) ? fact.trigger : [];
  if (triggers.length) {
    const chips = triggers.map(t => `<span class="lrn-chip">${esc(String(t))}</span>`).join("");
    parts.push(_field("Trigger", `<span class="lrn-chips">${chips}</span>`));
  }
  return `<div class="lrn-section"><div class="lrn-section-title">Lesson</div>${parts.join("")}</div>`;
}

function _renderInjectionPreview(preview) {
  return `
    <div class="lrn-section">
      <div class="lrn-section-title">Injection preview</div>
      <div class="lrn-preview-label">Exactly what a worker's system prompt receives (after a blank line):</div>
      <pre class="lrn-preview">${esc(preview || "")}</pre>
    </div>`;
}

// episode: {failed_tool, error, fixed_tool, user_prompt} | null — see miner.find_self_corrections
function _renderEpisode(episode) {
  if (!episode) return "";
  const lines = [
    episode.failed_tool ? `Failed tool: ${episode.failed_tool}` : "",
    episode.error ? `Error: ${episode.error}` : "",
    episode.fixed_tool ? `Fixed tool: ${episode.fixed_tool}` : "",
  ].filter(Boolean).join("\n");
  if (!lines) return "";
  return _field("Episode", `<pre class="lrn-episode-pre">${esc(lines)}</pre>`);
}

// proof: {kind:"journal"} | {kind:"confirming_read", mutation_tool, read_tool} | null
// — see knowledge/learning/capture_gate.py:verified_success_proof
function _proofLine(proof) {
  if (!proof) return "Proof: not recorded";
  if (proof.kind === "journal") return "Verified: journal-applied mutation";
  if (proof.kind === "confirming_read") {
    return `Verified: ${proof.mutation_tool || "?"} then confirming ${proof.read_tool || "?"}`;
  }
  return "Proof: not recorded";
}

function _rediscoveryLine(fact) {
  const prov = fact.provenance || {};
  const models = Array.isArray(prov.models_seen) ? prov.models_seen : [];
  const connCount = Array.isArray(prov.connections_seen) ? prov.connections_seen.length : 0;
  const n = Number(fact.rediscovery_count || 1);
  const modelsStr = models.length ? models.join(", ") : "none recorded";
  return `Seen ${n}x - models: ${modelsStr} - ${connCount} connection(s)`;
}

// Judge votes are raw parsed panel verdicts (knowledge/learning/judges.py) — "pass" means
// the vote agreed with keeping the fact clean: isolation = platform fact with no tenant
// data; safety = not safety-relevant. Any other verdict (including a malformed one) fails.
function _isolationPass(v) {
  return !!v && v.platform_fact === true && v.contains_tenant_data === false;
}

function _safetyPass(v) {
  return !!v && v.safety_relevant === false;
}

function _renderVerdictGroup(votes, label, passFn) {
  const list = Array.isArray(votes) ? votes : [];
  if (!list.length) {
    return `<div class="lrn-verdict-group">
      <div class="lrn-verdict-label">${esc(label)}</div>
      <div class="lrn-verdict-empty">No verdict recorded</div>
    </div>`;
  }
  const rows = list.map(v => {
    const ok = passFn(v);
    const icon = ok ? "ic-check-circle" : "ic-x-circle";
    const cls = ok ? "lrn-vote-pass" : "lrn-vote-fail";
    const reason = esc(String((v && v.reason) || ""));
    return `<div class="lrn-verdict-row ${cls}"><i class="ic ic-sm ${icon}"></i><span class="lrn-verdict-reason">${reason}</span></div>`;
  }).join("");
  return `<div class="lrn-verdict-group">
    <div class="lrn-verdict-label">${esc(label)}</div>
    ${rows}
  </div>`;
}

function _judgedByLine(prov) {
  const model = prov && prov.judge_model;
  if (!model) return "";
  return `<div class="lrn-evidence-line">${esc("Judged by: " + String(model))}</div>`;
}

function _renderOverlap(overlap) {
  if (!overlap) return "";
  const excerpt = _truncate(overlap.gotcha || "", 140);
  return `<div class="lrn-overlap-warning">Similar ${esc(String(overlap.status || ""))} fact exists: ${esc(excerpt)}</div>`;
}

function _renderEvidence(data) {
  const fact = data.fact || {};
  const prov = fact.provenance || {};
  const parts = [`<div class="lrn-section-title">Evidence</div>`];

  const episodeHtml = _renderEpisode(data.episode);
  if (episodeHtml) parts.push(episodeHtml);

  parts.push(`<div class="lrn-evidence-line">${esc(_proofLine(data.proof))}</div>`);
  parts.push(`<div class="lrn-evidence-line">${esc(_rediscoveryLine(fact))}</div>`);
  const judgedByHtml = _judgedByLine(prov);
  if (judgedByHtml) parts.push(judgedByHtml);
  parts.push(_renderVerdictGroup(prov.isolation_verdict, "Isolation votes", _isolationPass));
  parts.push(_renderVerdictGroup(prov.safety_verdict, "Safety votes", _safetyPass));

  const overlapHtml = _renderOverlap(data.overlap);
  if (overlapHtml) parts.push(overlapHtml);

  // Static placeholders for review-card sections not yet backed by real data (counter-
  // evidence tracking and the Phase 4 eval gate) — always shown, not conditioned on
  // data.counter_evidence / data.eval_signal (both are server-side None today).
  parts.push(`<div class="lrn-evidence-line lrn-evidence-muted">Counter-evidence: not tracked yet</div>`);
  parts.push(`<div class="lrn-evidence-line lrn-evidence-muted">Eval gate: not yet run (Phase 4)</div>`);

  return `<div class="lrn-section">${parts.join("")}</div>`;
}

// ---------------------------------------------------------------------------
// Review history rendering
// ---------------------------------------------------------------------------

function _fmtDateOnly(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

// For an "edit" review, the audit row's evidence_snapshot is {before:{...}, after:{...}}
// (see LearnedFactsService.edit) -- surface only the CHANGED FIELD NAMES, never the values,
// so a lesson's before/after text isn't duplicated into the review log's own display.
function _reviewChangedLine(review) {
  if (review.action !== "edit") return "";
  const snapshot = review.evidence_snapshot;
  const after = snapshot && typeof snapshot === "object" ? snapshot.after : null;
  if (!after || typeof after !== "object") return "";
  const keys = Object.keys(after);
  if (!keys.length) return "";
  return `changed: ${keys.join(", ")}`;
}

// Every field here is a stored, operator/LLM-influenced string (action/reviewer/reason) --
// esc() on each piece individually, same as every other candidate-derived value on this card.
function _renderReviewRow(review) {
  const action = String((review && review.action) || "");
  const reviewer = String((review && review.reviewer) || "");
  const dateStr = _fmtDateOnly(review && review.created_at);
  const reason = review && review.reason ? String(review.reason) : "";
  const changed = _reviewChangedLine(review || {});
  return `<div class="lrn-review-row">
    <div class="lrn-review-line">
      <span class="lrn-review-badge">${esc(action)}</span>
      <span class="lrn-review-reviewer">${esc(reviewer)}</span>
      <span class="lrn-review-date">${esc(dateStr)}</span>
    </div>
    ${reason ? `<div class="lrn-review-reason">${esc(reason)}</div>` : ""}
    ${changed ? `<div class="lrn-review-changed">${esc(changed)}</div>` : ""}
  </div>`;
}

function _renderReviews(reviews) {
  const list = Array.isArray(reviews) ? reviews : [];
  const body = list.length
    ? list.map(_renderReviewRow).join("")
    : `<div class="lrn-review-empty">No review actions yet.</div>`;
  return `<div class="lrn-section">
    <div class="lrn-section-title">Review history</div>
    ${body}
  </div>`;
}

// ---------------------------------------------------------------------------
// Actions row + inline edit form
// ---------------------------------------------------------------------------

function _actionButtonsHtml(fact) {
  const status = fact && fact.status;
  const btns = [];
  if (status === "candidate") {
    btns.push(`<button type="button" class="lrn-btn lrn-btn-primary" data-act="approve">Approve</button>`);
    btns.push(`<button type="button" class="lrn-btn" data-act="reject">Reject</button>`);
    btns.push(`<button type="button" class="lrn-btn" data-act="edit">Edit</button>`);
  } else if (status === "promoted") {
    btns.push(`<button type="button" class="lrn-btn" data-act="demote">Demote</button>`);
  }
  return `<div class="lrn-actions-row">${btns.join("")}</div>`;
}

function _widenControlHtml(fact) {
  // Offered only for a fact that is actually private -- widening a global fact is a no-op,
  // and an always-visible checkbox would invite ticking it out of habit.
  if (!fact || fact.status !== "candidate" || !isScoped(fact)) return "";
  return `<label class="lrn-widen">
    <input type="checkbox" id="lrn-widen-global">
    <span>Promote as global (visible to all connections)</span>
  </label>`;
}

function _actionsInnerHtml(fact) {
  // The single definition of "what lives in the actions wrap". _cancelEditForm restores this
  // wrap's contents too, and when it built its own copy from the buttons alone, cancelling an
  // edit silently dropped the widen checkbox -- so the two share one source instead.
  return `${_widenControlHtml(fact)}${_actionButtonsHtml(fact)}`;
}

function _actionsWrapHtml(fact) {
  return `<div class="lrn-actions-wrap">${_actionsInnerHtml(fact)}</div>`;
}

function _editFormHtml(fact) {
  const triggerCsv = Array.isArray(fact.trigger) ? fact.trigger.join(", ") : "";
  return `
    <div class="lrn-edit-form">
      <div class="lrn-field-label">Gotcha</div>
      <textarea class="lrn-edit-gotcha" rows="3">${esc(fact.gotcha || "")}</textarea>
      <div class="lrn-field-label">Workaround</div>
      <textarea class="lrn-edit-workaround" rows="3">${esc(fact.workaround || "")}</textarea>
      <div class="lrn-field-label">Trigger (comma-separated)</div>
      <input type="text" class="lrn-edit-trigger" value="${escAttr(triggerCsv)}" />
      <div class="lrn-edit-btns">
        <button type="button" class="lrn-btn lrn-btn-primary" data-act="edit-save">Save</button>
        <button type="button" class="lrn-btn" data-act="edit-cancel">Cancel</button>
      </div>
    </div>`;
}

// ---------------------------------------------------------------------------
// Card assembly
// ---------------------------------------------------------------------------

function _renderCard(data) {
  const fact = data.fact || {};
  _lastFact = fact;
  return `
    <div class="lrn-detail">
      <div class="lrn-detail-nav">
        <button class="lrn-back-btn" type="button">← Learning</button>
      </div>
      ${_renderHeader(fact)}
      <div class="lrn-detail-body">
        ${_renderLesson(fact)}
        ${_renderInjectionPreview(data.injection_preview)}
        ${_renderEvidence(data)}
        ${_renderReviews(data.reviews)}
        <div class="lrn-section">${_actionsWrapHtml(fact)}</div>
      </div>
    </div>`;
}

// ---------------------------------------------------------------------------
// Detail opener — monotonic token prevents a slow fetch from overwriting a
// fresher detail or the list after the user navigates Back / refreshes /
// opens a second row (mirrors run-errors.js's openErrorDetail).
// ---------------------------------------------------------------------------

export async function openLearningDetail(id) {
  const panel = _container();
  if (!panel) return;

  // Set synchronously so any external observer (e.g. the E2E test) sees it immediately.
  panel.dataset.activeFact = id;

  const body = panel.querySelector("#learningBody");
  if (!body) return;

  const token = ++_detailReq;
  body.innerHTML = `<div class="learning-empty">Loading…</div>`;

  try {
    const data = await api.learningDetail(id);
    if (token !== _detailReq) return;
    body.innerHTML = _renderCard(data);
  } catch (e) {
    if (token !== _detailReq) return;
    body.innerHTML = `<div class="learning-empty">Could not load lesson: ${esc(String(e.message || e))}</div>`;
  }
}

// ---------------------------------------------------------------------------
// Action handlers
// ---------------------------------------------------------------------------

function _showEditForm(fact) {
  const panel = _container();
  const wrap = panel && panel.querySelector(".lrn-actions-wrap");
  if (!wrap || !fact) return;
  wrap.innerHTML = _editFormHtml(fact);
}

function _cancelEditForm(fact) {
  const panel = _container();
  const wrap = panel && panel.querySelector(".lrn-actions-wrap");
  if (!wrap) return;
  wrap.innerHTML = _actionsInnerHtml(fact);
}

async function _submitApprove(id, fact) {
  // Read the widen checkbox BEFORE any await: the confirm dialogs yield to the event loop,
  // and a re-render in between would detach the node we are reading.
  const widenEl = document.getElementById("lrn-widen-global");
  const widen = !!(widenEl && widenEl.checked);

  if (fact && fact.safety_flagged) {
    const confirmed = await confirmTyped(
      "Promote safety-flagged lesson",
      "This lesson changes safety-relevant behavior. Type PROMOTE to confirm.",
      "PROMOTE",
    );
    if (!confirmed) return;
  } else {
    const scopeNote = widen
      ? " It will be marked GLOBAL and injected for every connection."
      : " It will be injected into future runs for this connection.";
    if (!window.confirm("Promote this lesson?" + scopeNote)) return;
  }
  const body = buildApproveBody(fact, widen);
  try {
    await api.learningAction(id, "approve", body);
    notify("Lesson promoted.", "success");
    refreshLearning();
  } catch (e) {
    notify(String(e.message || e), "error");
  }
}

async function _submitReject(id) {
  const reason = window.prompt("Reason (optional):");
  if (reason === null) return; // cancelled — leave the card open, no server call
  try {
    await api.learningAction(id, "reject", { reason });
    notify("Lesson rejected.", "success");
    refreshLearning();
  } catch (e) {
    notify(String(e.message || e), "error");
  }
}

async function _submitDemote(id) {
  const reason = window.prompt("Reason (optional):");
  if (reason === null) return; // cancelled
  try {
    await api.learningAction(id, "demote", { reason });
    notify("Returned to candidates for re-review.", "success");
    _tab = "candidate"; // the fact now lives in the candidate queue -- land where it is
    refreshLearning();
  } catch (e) {
    notify(String(e.message || e), "error");
  }
}

async function _submitEdit(id) {
  const panel = _container();
  const wrap = panel && panel.querySelector(".lrn-actions-wrap");
  if (!wrap) return;
  const gotcha = wrap.querySelector(".lrn-edit-gotcha")?.value ?? "";
  const workaround = wrap.querySelector(".lrn-edit-workaround")?.value ?? "";
  const triggerCsv = wrap.querySelector(".lrn-edit-trigger")?.value ?? "";
  const trigger = triggerCsv.split(",").map(s => s.trim()).filter(Boolean);
  try {
    await api.learningAction(id, "edit", { gotcha, workaround, trigger });
    notify("Lesson updated.", "success");
    openLearningDetail(id); // the one action that re-opens the card instead of the list
  } catch (e) {
    notify(String(e.message || e), "error");
  }
}

function _handleAction(act) {
  const panel = _container();
  const id = panel && panel.dataset.activeFact;
  if (!id) return;

  if (act === "edit") { _showEditForm(_lastFact); return; }
  if (act === "edit-cancel") { _cancelEditForm(_lastFact); return; }
  if (act === "edit-save") { _submitEdit(id); return; }
  if (act === "approve") { _submitApprove(id, _lastFact); return; }
  if (act === "reject") { _submitReject(id); return; }
  if (act === "demote") { _submitDemote(id); return; }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Wire the panel's event listeners.
 * Safe to call multiple times (guarded by _wired flag on the element).
 */
export function initLearningPanel() {
  const el = _container();
  if (!el || el._wired) return;
  el._wired = true;
  el.addEventListener("click", _onClick);
  document.addEventListener("click", (e) => {
    if (e.target.closest && e.target.closest("#btnRefreshLearning")) refreshLearning();
  }, { capture: false });
}

/** Called whenever the panel becomes the active left-zone panel. */
export function onLearningActivated() {
  refreshLearning();
}

/** Fetch the active tab's fact list and re-render the panel body. */
export async function refreshLearning() {
  const el = _container();
  if (!el) return;
  const body = el.querySelector("#learningBody");
  if (!body) return;

  // Supersede any in-flight detail fetch so a slow learningDetail() can't overwrite
  // the list after the user navigates Back / switches tabs / refreshes.
  _detailReq++;
  delete el.dataset.activeFact;

  setPanelLoading("learning", true);
  try {
    const data = await api.learningList(_tab);
    const facts = (data && Array.isArray(data.facts)) ? data.facts : [];
    body.innerHTML = _tabsHtml() + _renderList(facts, _tab);
  } catch (e) {
    body.innerHTML = _tabsHtml() + `<div class="learning-empty">Could not load candidate lessons: ${esc(String(e.message || e))}</div>`;
  } finally {
    setPanelLoading("learning", false);
  }
}

// ---------------------------------------------------------------------------
// Click handler (delegated — tabs, row drill-in, back nav, action buttons)
// ---------------------------------------------------------------------------

function _onClick(ev) {
  const tabBtn = ev.target.closest(".lrn-tab");
  if (tabBtn) {
    const tab = tabBtn.dataset.tab;
    if (tab && tab !== _tab) {
      _tab = tab;
      refreshLearning();
    }
    return;
  }

  if (ev.target.closest(".lrn-back-btn")) {
    refreshLearning();
    return;
  }

  const actBtn = ev.target.closest("[data-act]");
  if (actBtn) {
    _handleAction(actBtn.dataset.act);
    return;
  }

  const row = ev.target.closest(".lrn-row");
  if (row) {
    const learningId = row.dataset.learningId;
    if (learningId) openLearningDetail(learningId);
  }
}
